// « Ce qui sort de l'ordinaire » — the month's facts as the VALIDATED
// grille mensuelle (version paie) reports them, per employee: chips in
// the salary table and a month-wide panel above it. Deducted facts carry
// their amount at 1/22 of the reference salary (server rule, see
// server/src/services/payrollGrid.ts); nothing shows until the grille is
// validated.

import {
  PAYROLL_DAY_DIVISOR,
  type PayrollEmployeeDTO,
  type PayrollFactDTO,
  type PayrollFactKind,
} from '@pointage/shared';
import { fmtDA } from '../../lib/format.js';

const MONTH_SHORT = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

/** ['2026-10-12','2026-10-13','2026-10-14','2026-10-20'] → « 12–14, 20 oct. » */
export function dayRanges(days: readonly string[]): string {
  if (days.length === 0) return '';
  const nums = days.map((d) => Number(d.slice(8, 10)));
  const parts: string[] = [];
  let a = nums[0]!;
  let b = a;
  for (let i = 1; i <= nums.length; i++) {
    const n = nums[i];
    if (n === b + 1) { b = n; continue; }
    parts.push(a === b ? `${a}` : `${a}–${b}`);
    if (n != null) { a = n; b = n; }
  }
  return `${parts.join(', ')} ${MONTH_SHORT[Number(days[0]!.slice(5, 7)) - 1]}`;
}

const SHORT: Record<PayrollFactKind, string> = {
  absnj: 'non justifié',
  absj: 'justifié',
  maladie: 'maladie',
  entree: '',
  sortie: '',
  conge: 'congé',
  recup: 'récup',
  tt: 'TT',
};

function chipText(f: PayrollFactDTO): string {
  const n = f.days.length;
  if (f.kind === 'entree') return `Entrée · ${n} j avant`;
  if (f.kind === 'sortie') return `Sortie · ${n} j après`;
  // A named justified absence (RDV perso, Formation…) reads by its reason.
  if (f.kind === 'absj' && f.label !== 'Absence justifiée') return `${n} j ${f.label}`;
  return `${n} j ${SHORT[f.kind]}`;
}

export function factAmount(e: PayrollEmployeeDTO, f: PayrollFactDTO): number {
  return f.deducted ? (e.referenceSalary / PAYROLL_DAY_DIVISOR) * f.days.length : 0;
}

/** The « Ce mois » cell of the salary table. */
export function FactChips({
  e,
  open,
  onToggle,
}: {
  e: PayrollEmployeeDTO;
  open: boolean;
  onToggle: () => void;
}) {
  if (e.gridState === 'pending') return <span className="sb-factwait">En attente de la grille validée</span>;
  if (e.gridState === 'hors_pointage') return <span className="sb-factwait">Hors pointage</span>;
  const facts = e.monthFacts?.facts ?? [];
  if (facts.length === 0) return <span className="sb-factnone">Rien à signaler</span>;
  return (
    <div className="sb-chips">
      {facts.map((f) => (
        <button
          key={f.kind + f.label}
          type="button"
          className={'sb-fchip k-' + f.kind}
          aria-expanded={open}
          title={`${f.label} · ${dayRanges(f.days)}${f.deducted ? ' · retiré' : ' · payé'}`}
          onClick={onToggle}
        >
          {chipText(f)}
        </button>
      ))}
    </div>
  );
}

/** Expanded detail under a row: every fact with its dates and amount. */
export function FactDetail({ e }: { e: PayrollEmployeeDTO }) {
  const facts = e.monthFacts?.facts ?? [];
  const rate = e.referenceSalary / PAYROLL_DAY_DIVISOR;
  return (
    <div className="sb-fdetail">
      {facts.map((f) => (
        <div key={f.kind + f.label} className="sb-fdetail-row">
          <span className={'sb-fdot k-' + f.kind} />
          <b>{f.label}</b>
          <span className="sb-fdetail-days">{f.days.length} j · {dayRanges(f.days)}</span>
          {f.deducted ? (
            <span className="sb-fded gm-mono">
              {f.days.length} × {fmtDA(rate)} = −{fmtDA(factAmount(e, f))}
            </span>
          ) : (
            <span className="sb-fpaid">payé</span>
          )}
        </div>
      ))}
    </div>
  );
}

const CATEGORIES: Array<{ title: string; kinds: PayrollFactKind[] }> = [
  { title: 'Absences non justifiées', kinds: ['absnj'] },
  { title: 'Absences justifiées', kinds: ['absj'] },
  { title: 'Maladie', kinds: ['maladie'] },
  { title: 'Entrées / sorties', kinds: ['entree', 'sortie'] },
  { title: 'Congés (payés)', kinds: ['conge'] },
  { title: 'Récup · télétravail (payés)', kinds: ['recup', 'tt'] },
];

/** Month-wide summary above the salary table. Renders only for the
 * employees whose grille is validated. */
export function MonthFactsPanel({ team, monthName }: { team: PayrollEmployeeDTO[]; monthName: string }) {
  const validated = team.filter((e) => e.gridState === 'validated');
  if (validated.length === 0) return null;
  const quiet = validated.filter((e) => (e.monthFacts?.facts.length ?? 0) === 0);
  return (
    <section className="sb-facts" aria-labelledby="sb-facts-h">
      <div className="sb-facts-head">
        <h2 id="sb-facts-h">Ce qui sort de l'ordinaire en {monthName}</h2>
        <span className="sb-facts-src">Source : grille mensuelle validée, version paie</span>
      </div>
      <div className="sb-facts-grid">
        {CATEGORIES.map((c) => {
          const items = validated.flatMap((e) =>
            (e.monthFacts?.facts ?? []).filter((f) => c.kinds.includes(f.kind)).map((f) => ({ e, f })),
          );
          return (
            <div key={c.title} className="sb-fcat">
              <div className="sb-fcat-h">
                <span className={'sb-fdot k-' + c.kinds[0]} />
                {c.title}
                <span className="sb-fcat-n gm-mono">{items.length}</span>
              </div>
              {items.length === 0 ? (
                <div className="sb-fcat-empty">Aucun ce mois-ci</div>
              ) : (
                items.map(({ e, f }) => (
                  <div key={e.id + f.kind + f.label} className="sb-fitem">
                    <b>{e.name}</b>
                    <span>{f.kind === 'absj' && f.label !== 'Absence justifiée' ? `${f.days.length} j · ${f.label}` : `${f.days.length} j`}</span>
                    <span className="sb-fitem-d">{dayRanges(f.days)}</span>
                    {f.deducted ? <span className="sb-fded gm-mono">−{fmtDA(factAmount(e, f))}</span> : null}
                  </div>
                ))
              )}
            </div>
          );
        })}
      </div>
      <p className="sb-facts-rule">
        <b>Salaire du mois</b> = salaire habituel − salaire habituel ÷ {PAYROLL_DAY_DIVISOR} × jours non travaillés.
        Retirés : maladie, absences justifiées ou non, jours avant l'entrée / après la sortie. Payés : congé, récup, télétravail, fériés.
        {quiet.length > 0 && <> Rien à signaler : {quiet.map((e) => e.name).join(', ')}.</>}
      </p>
    </section>
  );
}
